import * as React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
// graphql
import { PLAN_KPI_UPDATE_MUTATION } from 'graphql/planKPIs/mutations'
import { PLAN_ACTION_UPDATE_MUTATION } from 'graphql/planActions/mutations'
import { PLAN_OBJECTIVE_UPDATE_MUTATION } from 'graphql/planObjectives/mutations'
// hooks
import { useCurrentUser } from 'hooks/useCurrentUser'
import { usePlanItemsTasks } from './usePlanItemTasks'
import { usePlanItemsReviews } from './usePlanItemReviews'
// types
import type { Moment } from 'moment'
import { IPlan, IPosition, IUser } from 'types/graphql'
// utils
import { map, pipe } from 'lodash/fp'
import { get, isEmpty } from 'lodash'
import { generatePath } from 'utils/tree'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'
import { safeGetOr, generateResultTarget } from 'utils/index'

// types
export interface ITask {
  id: string
  currentPermissions: any
  description: string
  entityType: string
  type: string
  dueDate: string | Moment
  isOverdue: boolean
  isOpen: boolean
  action?: {
    completionPercentage: number
  }
  review?: any
  period?: any
  assignedPersons: IUser[] | IPosition[]
  fieldId?: string
  path: string
}

export declare namespace NTasksListAPI {
  interface IContext {
    plan: IPlan
    data: ITask[]
    updateHandlers: {
      handleUpdateKPI: (task: ITask, capturedResults: any) => void
      handleUpdateAction: (task: ITask) => void
      handleUpdateObjective: (task: ITask) => void
    }
  }

  interface IIncomingProps {
    plan: IPlan
    children?: React.ReactNode
  }
}

export const TasksListAPIContext = React.createContext<NTasksListAPI.IContext>({
  plan: {},
  data: []
} as any)

const TasksListAPI: React.FunctionComponent<NTasksListAPI.IIncomingProps> = ({ plan, children }) => {
  const user = useCurrentUser()

  const [updateKPI] = useMutation(PLAN_KPI_UPDATE_MUTATION)
  const [updateAction] = useMutation(PLAN_ACTION_UPDATE_MUTATION)
  const [updateObjective] = useMutation(PLAN_OBJECTIVE_UPDATE_MUTATION)

  const controlledIds = [...user.controlledIds, ...user.userAndPositionIds]

  const filterUncontrolledTasks = (task) => {
    if (!isEmpty(task.assignedPersons)) {
      return task.assignedPersons.some(({ id }) => controlledIds.includes(id))
    }

    return controlledIds.includes(plan.ownedById)
  }

  const handleUpdateAction = async (task) => {
    try {
      const completionPercentage = get(task, 'action.completionPercentage') === 100 ? 0 : 100

      await updateAction({
        variables: {
          id: task.id,
          input: {
            fieldValues: [{ fieldId: task.fieldId, value: completionPercentage }],
            completionPercentage
          }
        }
      })
    } catch (error) {
      console.log('[TasksListAPI.handleUpdateAction]: ', error, parseGraphQLErrorMessages(error))
    }
  }

  const handleUpdateKPI = async (task, capturedResults) => {
    try {
      const resultTarget = generateResultTarget(task, capturedResults, task.ranges)

      await updateKPI({
        variables: {
          id: task.id,
          input: {
            fieldValues: [{ fieldId: task.fieldId, value: resultTarget }],
            resultTarget
          }
        }
      })
    } catch (error) {
      console.log('[TasksListAPI.handleUpdateKPI]: ', error, parseGraphQLErrorMessages(error))
    }
  }

  const handleUpdateObjective = async (task) => {
    try {
      await updateObjective({
        variables: {
          id: task.id,
          input: {
            fieldValues: [{ fieldId: task.fieldId, value: get(task, 'action.completionPercentage') === 100 ? 0 : 100 }]
          }
        }
      })
    } catch (error) {
      console.log('[TasksListAPI.handleUpdateObjective]: ', error, parseGraphQLErrorMessages(error))
    }
  }

  const planItems = React.useMemo(() => {
    return pipe(
      safeGetOr<any, any, any>([], 'planItems.data'),
      map((item: any) => ({
        ...item,
        path: generatePath({ ...item, plan: { name: get(item, 'plan.name') || plan.name } })
      }))
    )(plan)
  }, [plan])

  const { planKPIs, planActions, planObjectives } = usePlanItemsTasks(planItems, plan)
  const { planReviews, planObjectiveReviews, planActionReviews, planKPIReviews } = usePlanItemsReviews(planItems, plan)

  const data = [
    ...planKPIs,
    ...planActions,
    ...planReviews,
    ...planObjectives,
    ...planKPIReviews,
    ...planActionReviews,
    ...planObjectiveReviews
  ].filter(filterUncontrolledTasks)

  const contextValue = {
    plan,
    data,
    updateHandlers: {
      handleUpdateAction,
      handleUpdateKPI,
      handleUpdateObjective
    }
  }

  return <TasksListAPIContext.Provider value={contextValue}>{children}</TasksListAPIContext.Provider>
}

export default TasksListAPI
